import {
    getInterpolatedLineLayerWidth,
    getInterpolatedSelectableLineLayerWidth,
    getLineLayerConfig,
    getSelectableLineSortKey,
    INTERPOLATION_ZOOM_MAX,
    MAX_LINE_OFFSET,
    MIN_LINE_OFFSET,
    SELECTED_LINE_LAYER_SIZE,
} from "@l2r-front/l2r-map"
import { selectLayerColorOpacity } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../common/constants/i18n"

import { SPEEDINGS_DEFAULT_ACTIVE_LAYER } from "../constants/urlParams"

export const speedingsLegend = [
    {
        minValue: 0,
        maxValue: 2.5,
        color: "DPE/rouge",
        label: "containers.speedingsLayer.legend.scale.veryHigh",
    },
    {
        minValue: 2.5,
        maxValue: 5,
        color: "DPE/orange",
        label: "containers.speedingsLayer.legend.scale.high",
    },
    {
        minValue: 5,
        maxValue: 7.5,
        color: "DPE/jaune",
        label: "containers.speedingsLayer.legend.scale.medium",
    },
    {
        minValue: 7.5,
        maxValue: 10,
        color: "DPE/vert-foncé",
        label: "containers.speedingsLayer.legend.scale.low",
    },
]

export const getSpeedingsLegendConfig = (t, theme) => {
    return speedingsLegend.map(legendItem => {
        return {
            color: theme.palette[legendItem.color]?.main,
            label: t(I18N_NAMESPACE, legendItem.label),
        }
    })
        .reverse()
}

export const getSpeedingsLayerStyle = (referentialColor, theme, selectedRoad = null, currentYear = null, currentSpeedLimit) => {
    const legendStyles = [
        ["==", ["get", "exceed5Pct", ["properties"]], 0],
        selectLayerColorOpacity(theme.palette[speedingsLegend[0].color].main, 0.5, selectedRoad),
    ].concat(speedingsLegend.flatMap((legendItem) => {
        const color = theme.palette[legendItem.color].main
        return [
            ["all", [">", ["get", "exceed5Pct", ["properties"]], legendItem.minValue], ["<=", ["get", "exceed5Pct", ["properties"]], legendItem.maxValue]],
            selectLayerColorOpacity(color, 0.5, selectedRoad),
        ]
    }))
    const defaultColor = theme.palette[speedingsLegend[speedingsLegend.length - 1].color].main
    legendStyles.push(selectLayerColorOpacity(defaultColor, 0.5, selectedRoad))
    const speedLimitFilter = currentSpeedLimit !== SPEEDINGS_DEFAULT_ACTIVE_LAYER
        ? ["any",
            ["all",
                ["has", "speedDirections", ["properties"]],
                ["has", "+", ["get", "speedDirections", ["properties"]]],
                [
                    "==",
                    ["get", "speed_limit", ["get", "+", ["get", "speedDirections", ["properties"]]]],
                    parseInt(currentSpeedLimit),
                ],
            ],
            ["all",
                ["has", "speedDirections", ["properties"]],
                ["has", "-", ["get", "speedDirections", ["properties"]]],
                [
                    "==",
                    ["get", "speed_limit", ["get", "-", ["get", "speedDirections", ["properties"]]]],
                    parseInt(currentSpeedLimit),
                ],
            ],
        ]
        : null
    return getLineLayerConfig({
        "paint": {
            "line-color": [
                "case",
                ["!=", ["typeof", ["get", "exceed5Pct", ["properties"]]], "number"],
                selectLayerColorOpacity(referentialColor, referentialColor === "#FFFFFF" ? 0.85 : 0.5, selectedRoad),
            ].concat(legendStyles),
            "line-width": getInterpolatedSelectableLineLayerWidth(selectedRoad),
        },
        "layout": {
            "line-sort-key": getSelectableLineSortKey(selectedRoad),
        },
        "filter": ["all",
            ["all",
                ["!=", ["get", "road", ["at", 0, ["get", "linearLocation"]]], "string"],
                ["!=", ["get", "road", ["at", 0, ["get", "linearLocation"]]], selectedRoad]],
            ["==", ["get", "year", ["properties"]], parseInt(currentYear)],
            speedLimitFilter,
        ].filter(Boolean),
    })
}

export const getRoadSpeedingsLayerStyle = (referentialColor, theme, selectedRoad = null, currentSpeedLimit) => {
    const legendStyles = [
        ["==", ["get", "exceed5Pct", ["properties"]], 0],
        theme.palette[speedingsLegend[0].color].main,
    ].concat(speedingsLegend.flatMap((legendItem) => {
        const color = theme.palette[legendItem.color].main
        const conditions = ["all",
            [">", ["get", "exceed5Pct", ["properties"]], legendItem.minValue],
            ["<=", ["get", "exceed5Pct", ["properties"]], legendItem.maxValue],
        ]
        if (currentSpeedLimit !== SPEEDINGS_DEFAULT_ACTIVE_LAYER) {
            conditions.push(["==", ["get", "speedLimit", ["properties"]], parseInt(currentSpeedLimit)])
        }
        return [
            conditions,
            color,
        ]
    }))
    legendStyles.push(referentialColor)

    const getOffset = (offset) => {
        return ["case",
            ["!=", ["typeof", ["get", "exceed5Pct", ["properties"]]], "number"],
            0,
            offset,
        ]
    }

    return getLineLayerConfig({
        "paint": {
            "line-color": [
                "case",
                ["!=", ["typeof", ["get", "exceed5Pct", ["properties"]]], "number"],
                referentialColor,
            ].concat(legendStyles),
            "line-width": getInterpolatedLineLayerWidth(SELECTED_LINE_LAYER_SIZE),
            "line-offset":
                ["interpolate",
                    ["linear"],
                    ["zoom"],
                    1,
                    getOffset(MIN_LINE_OFFSET),
                    12,
                    getOffset(MIN_LINE_OFFSET),
                    INTERPOLATION_ZOOM_MAX,
                    getOffset(MAX_LINE_OFFSET),
                ],
        },
        "layout": {
            "line-sort-key": getSelectableLineSortKey(selectedRoad),
        },
    })
}

